import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Plus, Diamond, ChevronRight, Calendar, User, Trash2, Search, Settings, Send, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';

const STATUS_CONFIG = {
  saved:       { label: 'Saved',       bg: 'bg-gray-100',    text: 'text-gray-600' },
  allocated:   { label: 'Allocated',   bg: 'bg-amber-100',   text: 'text-amber-700' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100',    text: 'text-blue-700' },
  completed:   { label: 'Completed',   bg: 'bg-emerald-100', text: 'text-emerald-700' },
};
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SHADE_COLORS = {
  Fancy: 'bg-purple-100 text-purple-700',
  Lb: 'bg-blue-100 text-blue-700',
  Lc: 'bg-cyan-100 text-cyan-700',
  White: 'bg-gray-100 text-gray-700',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function DiamondSeries() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const role = currentUser?.role;

  const canCreate = ['admin', 'production_supervisor', 'diamond_dept', 'worker'].includes(role);
  const canDelete = role === 'admin';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/diamond-series?limit=100');
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load diamond series');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this series? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete(`/diamond-series/${id}`);
      toast.success('Series deleted');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      item.series_number?.toLowerCase().includes(q) ||
      item.manual_series_no?.toLowerCase().includes(q) ||
      item.colour?.toLowerCase().includes(q) ||
      item.clarity?.join(' ').toLowerCase().includes(q) ||
      item.shape_type_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/messenger')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronRight className="w-5 h-5 rotate-180 text-gray-600" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <Diamond className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-semibold text-gray-900">Diamond Series</h1>
          </div>
          {canCreate && (
            <Button
              data-testid="new-series-btn"
              onClick={() => navigate('/diamond-series/new')}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
            >
              <Plus className="w-4 h-4" />
              New Series
            </Button>
          )}
          {role === 'admin' && (
            <button
              data-testid="masters-btn"
              onClick={() => navigate('/diamond-series/masters')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-600"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="series-search"
              placeholder="Search by number, colour, clarity..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm bg-gray-50"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Diamond className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {search ? 'No series match your search' : 'No diamond series yet'}
            </p>
            {canCreate && !search && (
              <Button
                onClick={() => navigate('/diamond-series/new')}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Create First Series
              </Button>
            )}
          </div>
        ) : (
          filtered.map(item => (
            <div
              key={item.id}
              data-testid={`series-card-${item.id}`}
              onClick={() => navigate(`/diamond-series/${item.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Series numbers */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-blue-700 text-base">{item.series_number}</span>
                    {item.manual_series_no && (
                      <span className="text-gray-400 text-sm">#{item.manual_series_no}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SHADE_COLORS[item.shade] || 'bg-gray-100 text-gray-600'}`}>
                      {item.shade}
                    </span>
                    {item.status && STATUS_CONFIG[item.status] && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CONFIG[item.status].bg} ${STATUS_CONFIG[item.status].text}`}>
                        {STATUS_CONFIG[item.status].label}
                      </span>
                    )}
                  </div>

                  {/* Quality info */}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="font-semibold text-gray-800">{item.colour}</span>
                    {item.clarity?.map(c => (
                      <span key={c} className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{c}</span>
                    ))}
                    {item.shape_type_name && (
                      <span className="text-xs text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded">{item.shape_type_name}</span>
                    )}
                  </div>

                  {/* Codes */}
                  {item.codes_used && (
                    <p className="text-xs text-gray-500 mt-1 truncate">Codes: {item.codes_used}</p>
                  )}

                  {/* Totals */}
                  <div className="flex items-center gap-4 mt-2">
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Total Carats</p>
                      <p className="font-bold text-gray-900">{Number(item.grand_total_weight_ct).toFixed(3)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Total Pieces</p>
                      <p className="font-bold text-gray-900">{Math.round(item.grand_total_pieces).toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Categories</p>
                      <p className="font-bold text-gray-900">{item.categories?.length || 0}</p>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.date_of_issue)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {item.submitted_by_name}
                    </span>
                    {item.allocated_to_name && (
                      <span className="flex items-center gap-1 text-blue-500">
                        <Send className="w-3 h-3" />
                        {item.allocated_to_name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  {canDelete && (
                    <button
                      data-testid={`delete-series-${item.id}`}
                      onClick={e => handleDelete(item.id, e)}
                      disabled={deleting === item.id}
                      className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
